import globals from "@lvt/globals";
import { createApi } from "@reduxjs/toolkit/query/react";

import { vmsBaseQuery } from "@/api/vmsBase";

// Interfaces
export interface RequestedVideo {
    /**
     * An id that can be for a camera or archive cabinet.
     */
    camOrAcId: number;
    /**
     * The url for the dewarped video. Video playback will either use this or the videoUrl.
     */
    dewarpedVideoUrl?: string;
    /**
     * Id for the requested video itself.
     */
    id: number;
    /**
     * Flag to check if the requested video comes from an archive cabinet.
     */
    isArchiveCabinet: boolean;
    /**
     * Flag to display in the UI if the video comes from a thermal camera.
     */
    isThermal: boolean;
    /**
     * The city where the video was recorded.
     */
    locationCity?: string;
    /**
     * The county where the video was recorded.
     */
    locationCounty?: string;
    /**
     * The country where the video was recorded.
     */
    locationCountry?: string;
    /**
     * The name of the location where the video was recorded.
     */
    locationName?: string;
    /**
     * The state where the video was recorded.
     */
    locationState?: string;
    /**
     * The street address where the video was recorded.
     */
    locationStreet?: string;
    /**
     * The timezone where the video was recorded.
     */
    locationTimezone?: string;
    /**
     * The zipcode where the video was recorded.
     */
    locationZip?: string;
    /**
     * The serial of the live unit.
     */
    liveUnitSerialNumber?: string;
    /**
     * The name of the camera or archive cabinet.
     */
    name?: string;
    /**
     * ISO datetime when the video was recorded.
     */
    recordedDatetime?: string;
    /**
     * ISO datetime when the video was requested.
     */
    requestedDatetime?: string;
    /**
     * The user who requested the video.
     */
    requestedByName?: string;
    /**
     * The email of the user who requested the video.
     */
    requestedByEmail?: string;
    /**
     * The url for the video. Video playback will either use this or the dewarpedVideoUrl.
     */
    videoUrl?: string;
}

export enum RequestType {
    LiveUnit = "live_unit",
}

// Helpers
export const isRequestedVideo = (value: RequestedVideo[] | Error): value is RequestedVideo[] => {
    return Array.isArray(value) && value.every((data) => data.hasOwnProperty("camOrAcId"));
};

export interface RequestVideosLiveUnitParams {
    liveUnitId: number;
}

export interface RequestVideoShareLinkParams {
    dewarped: boolean;
    liveUnitId: number;
    videoId: number;
}

export const requestedVideosApi = createApi({
    reducerPath: "requestedVideosApi",
    baseQuery: vmsBaseQuery(globals.userdashboard.proxyUrl),
    endpoints: (builder) => ({
        fetchRequestedVideos: builder.query<RequestedVideo[], RequestVideosLiveUnitParams>({
            query: ({ liveUnitId }) => ({
                url: `/api/vms/requested_videos/${liveUnitId}?request_type=${RequestType.LiveUnit}`,
                method: "GET",
            }),
        }),
        getShareLink: builder.query<string, RequestVideoShareLinkParams>({
            query: ({ dewarped, liveUnitId, videoId }) => ({
                url: `/api/vms/requested_videos/${liveUnitId}/${videoId}:share?dewarped=${dewarped}`,
                method: "GET",
            }),
        }),
    }),
});

export const { useFetchRequestedVideosQuery, useLazyGetShareLinkQuery } = requestedVideosApi;
